/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { SearchApi } from '@alfresco/js-api';
import { forkJoin, from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DmsField, DmsMetadataService, DmsOption } from '../upload/dms-metadata.service';

/** Ceiling for the per-type document counts; see `AllDocumentsService`. */
const COUNT_LIMIT = 1000;

const SCOPE = '(TYPE:"cm:content" OR TYPE:"cm:folder") AND NOT TYPE:"cm:dictionaryModel"';

export interface DocumentTypeRow {
  /** The value stored in `dms:documentType`. */
  value: string;
  label: string;
  /** Documents currently carrying this type. */
  documentCount: number;
  /** False when the count hit {@link COUNT_LIMIT} and is a lower bound. */
  countExact: boolean;
  /** A type is "in use" once at least one document carries it. */
  inUse: boolean;
}

/** One property of the shared metadata schema, as the repository defines it. */
export interface SchemaField {
  id: string;
  label: string;
  dataType: string;
  required: boolean;
  defaultValue: string;
  /** Allowed values for a LIST-constrained property, code and display text. */
  options: DmsOption[];
}

export interface DocumentTypeCatalogue {
  types: DocumentTypeRow[];
  fields: SchemaField[];
  /**
   * Documents carrying the metadata aspect.
   *
   * Counted with `ASPECT:"dms:documentInfo"`, not `EXISTS:"dms:documentType"` —
   * this repository answers `EXISTS` on a custom property with zero hits.
   */
  classified: number;
  classifiedExact: boolean;
}

/** Thai labels for the schema's own property names. */
export const FIELD_LABELS: Record<string, string> = {
  'dms:documentCode': 'DMS.UI.5531B0',
  'dms:documentType': 'DMS.UI.BAA6B2',
  'dms:documentDate': 'DMS.UI.F827E3',
  'dms:department': 'DMS.UI.81A5EB',
  'dms:documentOwner': 'DMS.UI.CD9CC1',
  'dms:status': 'DMS.UI.773CBF',
  'dms:confidentiality': 'DMS.UI.8DD3DA'
};

/**
 * The document-type catalogue (UI-09).
 *
 * Everything here is read from the repository: the list of types is the LIST
 * constraint on `dms:documentType`, the field list is the `dms:documentInfo`
 * aspect, and each type's document count is a real search.
 *
 * The **fields** are read-only here: they live in
 * `server-extension/content-model/dmsModel.xml` and are deployed to Alfresco.
 * The **types** can be changed from the settings screen — see
 * {@link ContentModelAdminService}, which edits that same model in the
 * repository. Keep the file in this repository in step with any change made
 * that way, or the next deploy will undo it.
 */
@Injectable({ providedIn: 'root' })
export class DocumentTypesService {
  private readonly api = inject(AlfrescoApiService);
  private readonly dms = inject(DmsMetadataService);

  private get searchApi(): SearchApi {
    return new SearchApi(this.api.getInstance() as any);
  }

  /** The full catalogue: types with their usage, plus the shared field list. */
  catalogue(): Observable<DocumentTypeCatalogue> {
    return this.dms.schema().pipe(
      switchMap((schema) => {
        if (!schema.available) {
          return of({ types: [], fields: [], classified: 0, classifiedExact: true });
        }

        const options = schema.fields['dms:documentType']?.options ?? [];
        const fields = Object.values(schema.fields).map((field) => this.toField(field));

        return forkJoin({
          counts: options.length
            ? forkJoin(options.map((option) => this.count(`${SCOPE} AND =dms:documentType:"${option.value}"`)))
            : of([] as { value: number; exact: boolean }[]),
          classified: this.count(`${SCOPE} AND ASPECT:"dms:documentInfo"`)
        }).pipe(
          map(({ counts, classified }) => ({
            types: options.map((option, i) => ({
              value: option.value,
              label: option.label,
              documentCount: counts[i]?.value ?? 0,
              countExact: counts[i]?.exact ?? true,
              inUse: (counts[i]?.value ?? 0) > 0
            })),
            fields,
            classified: classified.value,
            classifiedExact: classified.exact
          }))
        );
      })
    );
  }

  /**
   * How many documents carry each value of one list field.
   *
   * Used before a value is taken out of the model: the repository accepts a
   * narrowed list without complaint, so the only thing standing between a
   * removed value and content nobody can read back is this count.
   */
  valueUsage(fieldId: string, values: string[]): Observable<Record<string, { value: number; exact: boolean }>> {
    if (!values.length) {
      return of({});
    }
    return forkJoin(values.map((value) => this.count(`${SCOPE} AND =${fieldId}:"${value}"`))).pipe(
      map((counts) => Object.fromEntries(values.map((value, index) => [value, counts[index]])))
    );
  }

  /**
   * Counts by asking for ids only, up to {@link COUNT_LIMIT}. `exact` is false
   * when the page filled, in which case the value is a lower bound.
   */
  private count(query: string): Observable<{ value: number; exact: boolean }> {
    return from(
      this.searchApi.search({
        query: { query, language: 'afts' },
        paging: { maxItems: COUNT_LIMIT, skipCount: 0 },
        fields: ['id']
      }) as Promise<any>
    ).pipe(
      map((res) => {
        const pagination = res?.list?.pagination ?? {};
        return { value: pagination.count ?? 0, exact: !pagination.hasMoreItems };
      })
    );
  }

  private toField(field: DmsField): SchemaField {
    return {
      id: field.id,
      // a field the model shipped with has its name here; one added from the
      // settings screen has it in the label store the metadata service reads
      label: FIELD_LABELS[field.id] ?? DmsMetadataService.label(field.id),
      dataType: field.dataType,
      required: field.mandatory,
      defaultValue: field.defaultValue ?? '',
      // the code is what the model stores and what a document carries; the label
      // is only how it reads, so both travel together
      options: field.options ?? []
    };
  }
}
